import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import CollectionCard from "@/components/CollectionCard";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { Plus, Loader2, Images } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Link } from "react-router-dom";

export default function MyCollections() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "" });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) navigate("/");
  }, [authLoading, user, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/collections/mine");
      setCollections(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) load();
  }, [user]);

  const create = async () => {
    if (!form.title.trim()) return;
    setCreating(true);
    try {
      const res = await api.post("/collections", { title: form.title.trim(), description: form.description });
      toast.success("Collection created");
      setOpen(false);
      setForm({ title: "", description: "" });
      navigate(`/collections/${res.data.id}`);
    } catch (e) {
      toast.error("Could not create collection");
    } finally {
      setCreating(false);
    }
  };

  if (authLoading || !user) return null;

  return (
    <div className="min-h-screen bg-parchment">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-serif font-bold text-ink">My Collections</h1>
            <p className="text-muted-foreground mt-1">Build boards, submit for review, and get published.</p>
          </div>
          <button
            data-testid="create-collection-button"
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-2 bg-brick hover:bg-brick-dark text-parchment px-5 py-2.5 rounded-full font-semibold transition-colors"
          >
            <Plus className="h-4 w-4" /> New Collection
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-brick" /></div>
        ) : collections.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-stoneline rounded-xl bg-white/50">
            <Images className="h-12 w-12 mx-auto text-stone-300" />
            <p className="font-serif text-xl text-ink mt-4">No collections yet</p>
            <p className="text-muted-foreground mt-1">Create your first collection, then add images from the archive.</p>
            <Link to="/search" className="inline-block mt-4 text-brick font-semibold hover:underline">Browse the archive →</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {collections.map((c) => (
              <CollectionCard key={c.id} collection={c} to={`/collections/${c.id}`} showStatus />
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="create-collection-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">New Collection</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Title</label>
              <Input
                data-testid="collection-title-input"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Rana-era Portraits"
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <Textarea
                data-testid="collection-desc-input"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="What is this collection about?"
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <button
              data-testid="save-collection-btn"
              onClick={create}
              disabled={creating || !form.title.trim()}
              className="inline-flex items-center gap-2 bg-brick hover:bg-brick-dark text-parchment px-5 py-2.5 rounded-full font-semibold transition-colors disabled:opacity-50"
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Create
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
